import { useEffect, useRef, useState } from "react";
import { Play, Volume2, VolumeX } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const WRAP = {
  banner: "w-full",
  block: "max-w-[900px] mx-auto px-6 md:px-0",
  inline: "max-w-[420px]",
};

// Autoplay-muted placements only run while on screen (saves bandwidth, no background playback).
const usePlayWhenVisible = (ref, enabled) => {
  useEffect(() => {
    if (!enabled || !ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) el.play().catch(() => {});
      else el.pause();
    }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, enabled]);
};

const usePlayback = (autoplay) => {
  const ref = useRef(null);
  const [started, setStarted] = useState(!!autoplay);
  const [muted, setMuted] = useState(!!autoplay);
  usePlayWhenVisible(ref, !!autoplay);
  const start = () => {
    setStarted(true);
    requestAnimationFrame(() => ref.current?.play().catch(() => {}));
  };
  const toggleMute = () => {
    if (!ref.current) return;
    ref.current.muted = !muted;
    setMuted(!muted);
  };
  return { ref, started, muted, start, toggleMute };
};

const PlayButton = ({ id, onClick }) => (
  <button type="button" onClick={onClick} aria-label="Play video" data-testid={`video-play-${id}`}
    className="absolute inset-0 flex items-center justify-center group">
    <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-[color:var(--brand-cream)]/90 text-ink flex items-center justify-center transition-transform group-hover:scale-105">
      <Play size={26} className="ml-1" fill="currentColor" />
    </span>
  </button>
);

const MuteButton = ({ id, muted, onClick }) => (
  <button type="button" onClick={onClick} aria-label={muted ? "Unmute" : "Mute"} data-testid={`video-mute-${id}`}
    className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
  </button>
);

const BannerCaption = ({ title, caption }) => (
  <div className="absolute left-6 md:left-12 bottom-6 md:bottom-10 pointer-events-none">
    <div className="font-serif text-3xl md:text-5xl font-light text-[color:var(--brand-cream)] drop-shadow-lg">{title}</div>
    {caption && <div className="text-[color:var(--brand-cream)]/80 mt-2 max-w-xl">{caption}</div>}
  </div>
);

export const VideoPlayer = ({ placement }) => {
  const { video, layout = "block", title, caption, autoplay } = placement;
  const { ref, started, muted, start, toggleMute } = usePlayback(autoplay);
  const src = `${BACKEND_URL}${video.stream_url}`;
  const poster = `${BACKEND_URL}${video.poster_url}`;
  const isBanner = layout === "banner";
  const ratio = isBanner ? "21 / 9" : (video.width && video.height ? `${video.width} / ${video.height}` : "16 / 9");

  return (
    <figure className={WRAP[layout] || WRAP.block} data-testid={`video-block-${placement.id}`}>
      {title && !isBanner && <div className="overline text-mute mb-3">{title}</div>}
      <div className={`relative bg-ink overflow-hidden ${isBanner ? "max-h-[85vh]" : ""}`} style={{ aspectRatio: ratio }}>
        <video
          ref={ref}
          className="absolute inset-0 w-full h-full object-cover"
          poster={poster}
          preload={autoplay ? "metadata" : "none"}
          playsInline
          loop={!!autoplay}
          muted={muted}
          autoPlay={!!autoplay}
          controls={started && !autoplay}
          src={started || autoplay ? src : undefined}
          data-testid={`video-el-${placement.id}`}
        />
        {!started && <PlayButton id={placement.id} onClick={start} />}
        {autoplay && <MuteButton id={placement.id} muted={muted} onClick={toggleMute} />}
        {title && isBanner && <BannerCaption title={title} caption={caption} />}
      </div>
      {caption && !isBanner && <figcaption className="text-sm text-ink-soft mt-3 leading-relaxed">{caption}</figcaption>}
    </figure>
  );
};
